// Packages
import { useContext, useMemo, useRef, useState } from "react";
import { useRecoilState } from "recoil";
import debounce from "lodash.debounce";

// Store
import { searchQuery } from "../../Store/Store";

// Context
import { Language } from "../../Language/Language";

// Elements
import { SearchContainer, SearchLabel, SearchField, InputWrapper, InputIcon, ClearButton } from "./SearchInput.elements";
import { IconSearch } from "./Results.icons";

export default function SearchInput({ atom = searchQuery, immediate = false }) {
  const { filters } = useContext(Language);
  const [query, setQuery] = useRecoilState(atom);
  const inputRef = useRef(null);
  const [hasValue, setHasValue] = useState(!!query);

  const handleSearchChange = (value) => {
    setQuery(value);
    if (value.length >= 3 && window.tuiTrack) {
      window.tuiTrack.trackEvent('click', `search:query:${value}`);
    }
  };

  // Stable debounced instance so it can be cancelled on clear.
  const debouncedHandleSearchChange = useMemo(
    () => debounce(handleSearchChange, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleInput = (value) => {
    setHasValue(!!value);
    // In the mobile sheet the results aren't updating live, so debouncing buys
    // nothing and could drop a keystroke if Confirm is tapped within 300ms.
    // Write the draft synchronously; only debounce the live (desktop) atom.
    if (immediate) {
      handleSearchChange(value);
    } else {
      debouncedHandleSearchChange(value);
    }
  };

  const handleClear = () => {
    debouncedHandleSearchChange.cancel();
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.focus();
    }
    setHasValue(false);
    setQuery("");
  };

  return (
    <SearchContainer>
      <SearchLabel>{filters.search || "Search restaurants:"}</SearchLabel>
      <InputWrapper>
        <InputIcon><IconSearch /></InputIcon>
        <SearchField
          ref={inputRef}
          type="text"
          placeholder={filters.searchPlaceholder || "Type at least 3 letters..."}
          defaultValue={query}
          onChange={(e) => handleInput(e.target.value)}
          onKeyUp={(e) => {
            if (e.target.value !== query) {
              handleInput(e.target.value);
            }
          }}
        />
        {hasValue && (
          <ClearButton
            type="button"
            onClick={handleClear}
            aria-label={filters.clear || "Clear"}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 1L13 13M13 1L1 13"
                stroke="#53565A"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </ClearButton>
        )}
      </InputWrapper>
    </SearchContainer>
  );
}
